import { pipe } from 'fp-ts/function';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { CommanderFormProps, useHandledDialogFormControls } from '@mews-commander/form';
import { DividerList } from '@mews-commander/ui';
import { Banner, Stack } from '@optimus-web/core';
import { DurationService, createExperimentalZodFormResolver, lt } from '@mews/framework';
import {
    BookablePeriod,
    CancellationPolicyApplicability,
    CancellationPolicyParameters,
    CancellationPolicyUpdateParameters,
    CancellationPolicyWithProfiles,
} from '@mews-commander/data-access';
import { toPercents } from '@mews-commander/utils';
import {
    useCancellationPolicyGetSuspenseQuery,
    useCancellationPolicyUpdateMutation,
    useCreateCancellationPolicy,
} from '../queries';
import {
    CancellationFeeExtentFieldOption,
    CancellationPolicyFormField,
    CancellationPolicyOffsetType,
    WithCancellationPolicyId,
} from '../types';
import { CancellationPolicyGeneralFormSection } from './CancellationPolicyGeneralFormSection';
import { CancellationPolicyFeesFormSection } from './CancellationPolicyFeesFormSection';
import { CancellationPolicyDeadlineFormSection } from './CancellationPolicyDeadlineFormSection';
import { FormValues, validationSchema } from './schema';

const getInitialValues = (data: CancellationPolicyWithProfiles) => {
    const offsetDuration = DurationService.parse(data.applicabilityOffset);
    return {
        ...data,
        [CancellationPolicyFormField.Applicability]: data.applicabilityType,
        [CancellationPolicyFormField.OffsetType]:
            data.applicabilityType !== CancellationPolicyApplicability.Creation &&
            (DurationService.isNegative(offsetDuration) || DurationService.isZero(offsetDuration))
                ? CancellationPolicyOffsetType.Before
                : CancellationPolicyOffsetType.After,
        [CancellationPolicyFormField.RelativeFee]: toPercents(data.relativeFee, 4),
        [CancellationPolicyFormField.ApplicabilityOffset]: pipe(offsetDuration, DurationService.absolute, (duration) =>
            DurationService.toObject(duration, { partial: false })
        ),
        [CancellationPolicyFormField.FeeExtent]:
            data.feeExtent.length > 1
                ? CancellationFeeExtentFieldOption.TimeUnitsAndProducts
                : CancellationFeeExtentFieldOption[data.feeExtent[0]],
    };
};

const useInitialValues = (data?: CancellationPolicyWithProfiles): FormValues => {
    const acceptedCurrencyCodes = useCurrentEnterprise().acceptedCurrencyCodes as string[];

    if (data) {
        return getInitialValues(data);
    }

    return {
        bookablePeriod: BookablePeriod.Night,
        relativeFee: 0,
        feeExtent: CancellationFeeExtentFieldOption.TimeUnitsAndProducts,
        absoluteFee: 0,
        absoluteFeeCurrencyCode: acceptedCurrencyCodes[0],
        applicabilityOffset: DurationService.toObject(DurationService.parse('PT0S'), { partial: false }),
        offsetType: CancellationPolicyOffsetType.After,
        applicability: CancellationPolicyApplicability.Creation,
    };
};

interface CancellationPolicyFormContentProps {
    shouldDisableBookablePeriodField?: boolean;
}

const CancellationPolicyFormContent = ({ shouldDisableBookablePeriodField }: CancellationPolicyFormContentProps) => (
    <DividerList spacing="400">
        <CancellationPolicyGeneralFormSection shouldDisableBookablePeriodField={shouldDisableBookablePeriodField} />
        <CancellationPolicyFeesFormSection />
        <CancellationPolicyDeadlineFormSection />
    </DividerList>
);

export const CreateCancellationPolicyFormContent = (props: CommanderFormProps) => {
    const initialValues = useInitialValues();

    const { mutateAsync } = useCreateCancellationPolicy();

    const { renderFormContent } = useHandledDialogFormControls<FormValues, CancellationPolicyParameters>({
        ...props,
        resolver: createExperimentalZodFormResolver(validationSchema),
        defaultValues: initialValues,
        onSubmit: async (values) => mutateAsync(values),
    });

    return renderFormContent(<CancellationPolicyFormContent />);
};

export const UpdateCancellationPolicyFormContent = ({
    cancellationPolicyId,
    ...formControls
}: WithCancellationPolicyId & CommanderFormProps) => {
    const { data } = useCancellationPolicyGetSuspenseQuery(cancellationPolicyId);
    const initialValues = useInitialValues(data);

    const { mutateAsync } = useCancellationPolicyUpdateMutation();

    const { renderFormContent } = useHandledDialogFormControls<
        FormValues,
        Omit<CancellationPolicyUpdateParameters, 'id'>
    >({
        ...formControls,
        resolver: createExperimentalZodFormResolver(validationSchema),
        defaultValues: initialValues,
        onSubmit: async (values) => mutateAsync({ ...values, id: cancellationPolicyId }),
    });

    return renderFormContent(
        <Stack vertical distribution="space-between" spacing="400">
            <Banner variant="info" primaryText={lt('CancellationPolicyUpdateFormInfoBanner')} />
            <CancellationPolicyFormContent shouldDisableBookablePeriodField />
        </Stack>
    );
};
